import {
  LayoutDashboard,
  Calendar,
  BarChart3,
  Play,
  Users,
  Trophy,
  Shield,
  type LucideIcon,
} from 'lucide-react';

export type PublicTabId =
  | 'scoreboard'
  | 'schedule'
  | 'standings'
  | 'teams'
  | 'playoffs'
  | 'bracket'
  | 'alive';

export type PublicTabDef = {
  id: PublicTabId;
  label: string;
  icon: LucideIcon;
};

const POOL_PLAY_TABS: PublicTabDef[] = [
  { id: 'scoreboard', label: 'Scoreboard', icon: LayoutDashboard },
  { id: 'schedule', label: 'Schedule', icon: Calendar },
  { id: 'standings', label: 'Standings', icon: BarChart3 },
  { id: 'teams', label: 'Teams', icon: Users },
  { id: 'playoffs', label: 'Playoffs', icon: Play },
];

const DKO_TABS: PublicTabDef[] = [
  { id: 'scoreboard', label: 'Scoreboard', icon: LayoutDashboard },
  { id: 'bracket', label: 'Bracket', icon: Trophy },
  { id: 'schedule', label: 'Schedule', icon: Calendar },
  { id: 'alive', label: 'Alive', icon: Shield },
  { id: 'teams', label: 'Teams', icon: Users },
];

export function isDkoPublicTournament(tournamentType: string | null | undefined): boolean {
  return tournamentType === 'double_elimination';
}

export function getPublicTabs(tournamentType: string | null | undefined): PublicTabDef[] {
  return isDkoPublicTournament(tournamentType) ? DKO_TABS : POOL_PLAY_TABS;
}

/** All tab ids that may appear in URLs (pool + DKO + legacy aliases). */
export const ALL_PUBLIC_TAB_IDS: readonly PublicTabId[] = [
  'scoreboard',
  'schedule',
  'standings',
  'teams',
  'playoffs',
  'bracket',
  'alive',
] as const;

/**
 * Normalize a raw ?tab= value for the current tournament format.
 * Legacy DKO links use playoffs/standings; map those to bracket/alive.
 */
export function resolvePublicTabId(
  rawTab: string | null | undefined,
  tournamentType: string | null | undefined
): PublicTabId | null {
  if (!rawTab) return null;
  if (!(ALL_PUBLIC_TAB_IDS as readonly string[]).includes(rawTab)) return null;

  const isDko = isDkoPublicTournament(tournamentType);
  const tab = rawTab as PublicTabId;

  if (isDko) {
    if (tab === 'playoffs') return 'bracket';
    if (tab === 'standings') return 'alive';
    // Pool-only tabs that somehow appear — keep if valid for DKO, else null
    const allowed = new Set(DKO_TABS.map(t => t.id));
    return allowed.has(tab) ? tab : null;
  }

  if (tab === 'bracket') return 'playoffs';
  if (tab === 'alive') return 'standings';
  const allowed = new Set(POOL_PLAY_TABS.map(t => t.id));
  return allowed.has(tab) ? tab : null;
}

export function getDefaultPublicTab(
  tournamentType: string | null | undefined,
  hasPlayoffSlots: boolean
): PublicTabId {
  if (isDkoPublicTournament(tournamentType) && hasPlayoffSlots) return 'bracket';
  return 'scoreboard';
}
